import type { CustomerDetailDto } from '@ecom/contracts';

/**
 * Small decisions about one customer that the profile page makes, kept out of
 * React so they can be tested.
 */

type Contactable = Pick<CustomerDetailDto, 'phone' | 'addresses'>;
type Nameable = Pick<CustomerDetailDto, 'id' | 'name' | 'email' | 'phone'>;

/**
 * The number to reach them on: the one they sign in with, or else the one on
 * their default address — the number a courier would ring. Null when neither.
 */
export function contactPhone(customer: Contactable): string | null {
  if (customer.phone) return customer.phone;
  const address = customer.addresses.find((entry) => entry.isDefault) ?? customer.addresses[0];
  return address?.phone || null;
}

/** Characters a file name cannot carry on one system or another. */
const UNSAFE = /[\\/:*?"<>|]+/g;

/**
 * The export's file name: who it is about and the day it was taken —
 * `customer-sara-alotaibi-2026-10-01.json` — so a folder of requests reads as
 * a list of people rather than of ids. The name if there is one, else the
 * email's local part, else the number; the id only when there is nothing else.
 */
export function exportFileName(customer: Nameable, today: Date): string {
  const label =
    slug(customer.name) ||
    slug(customer.email?.split('@')[0] ?? '') ||
    slug(customer.phone?.replace(/\D/g, '') ?? '') ||
    customer.id.slice(0, 8);
  return `customer-${label}-${localDay(today)}.json`;
}

/** `YYYY-MM-DD` in the operator's own calendar — the day they took it, not UTC's. */
function localDay(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

function slug(value: string): string {
  return value
    .normalize('NFC')
    .replace(UNSAFE, ' ')
    .trim()
    .toLowerCase()
    .replace(/[\s._]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}
